/* Rules Match engine — the proper turn-based TCG. Both sides field a 30 HP
   Commander, three lanes, and their six-card Arena deck as the whole hand.
   Scrap starts at 3 and grows +2 per round (banked scrap feeds Overclock).
   All seven core keywords fire for real; terrain twists the lane it holds. */
import { getCardRules } from "../data/tcgRules";
import { officialTcgCards } from "../data/site";
import { buildEnemyDeck, cardById, cardPower } from "./tcg";

export const MATCH_HP = 30;
export const MATCH_ROUNDS = 10;
export const SCRAP_START = 3;
export const SCRAP_PER_ROUND = 2;
export const SCRAP_CAP = 16;

const num = (v) => (typeof v === "number" ? v : v === "∞" ? 99 : 0);

/* match costs: printed scrap doubles as arena power, so lane-table costs scale —
   units pay sticker price (cap 16 keeps the 15-scrap bombs playable), terrain
   compresses to 4–10 so the big Runner nodes can actually hit the board */
export const matchCost = (card) => {
  const sticker = num(card.stats.scrap);
  return isTerrainCard(card) ? Math.min(10, Math.max(4, Math.round(sticker / 5))) : sticker;
};

let instSeq = 0;
const makeUnit = (card) => {
  instSeq += 1;
  // ∞-stat bombs (Echo / Volo / Singularity) enter Rules Matches as UNSTABLE —
  // a colossal but finite 20 ATK that bills its own commander 3 HP a round,
  // exactly as the core rules gate raw power behind Corrupted
  return { iid: `${card.id}#${instSeq}`, card, hp: num(card.stats.def), tempAtk: 0, jammed: false, bonusHp: 0, unstable: card.stats.atk === "∞" };
};

const kwOf = (unit) => (unit.jammed ? [] : getCardRules(unit.card).keywords);
const hasKw = (unit, name) => kwOf(unit).some((k) => k === name || k.startsWith(`${name} `));
const kwValue = (unit, name) => {
  const hit = kwOf(unit).find((k) => k.startsWith(`${name} `));
  return hit ? Number(hit.split(" ")[1]) || 1 : 1;
};

export const isTerrainCard = (card) => getCardRules(card).type === "TERRAIN";

/* ---------- THE WAR COUNCIL — five seats, five deck doctrines, climbed in order ---------- */
const byFaction = (f) => officialTcgCards.filter((c) => c.faction === f);
const cheapest = (pool, n) => [...pool].sort((a, b) => num(a.stats.scrap) - num(b.stats.scrap)).slice(0, n);
const strongest = (pool, n) => [...pool].sort((a, b) => cardPower(b) - cardPower(a)).slice(0, n);
const guardWalls = () =>
  officialTcgCards
    .filter((c) => c.faction === "WASTELAND VANGUARD" && !c.role.startsWith("TERRAIN") && num(c.stats.def) >= 20)
    .sort((a, b) => num(b.stats.def) - num(a.stats.def));

export const COUNCIL_PERSONAS = [
  {
    id: "sax",
    name: "WARDEN SAX",
    title: "Gatekeeper of the Mesh",
    style: "Scrapper swarm — cheap Salvage units that refund themselves.",
    income: 3,
    pts: 10,
    deck: () => cheapest(byFaction("SCRAPPER"), 6),
  },
  {
    id: "vane",
    name: "CIPHER VANE",
    title: "The Informant",
    style: "Black-Ops Net control — Jam, stealth, and council legendaries.",
    income: 4,
    pts: 12,
    deck: () => buildEnemyDeck(),
  },
  {
    id: "verge",
    name: "MOTHER VERGE",
    title: "The Broker",
    style: "Node economy — banks terrain income and buries you in scrap.",
    income: 4,
    pts: 14,
    deck: () => [
      ...cheapest(byFaction("SCRAPPER"), 3),
      ...cheapest(officialTcgCards.filter((c) => getCardRules(c).type === "TERRAIN" && terrainRule(c) === "node"), 3),
    ],
  },
  {
    id: "halcyon",
    name: "GENERAL HALCYON",
    title: "The Wall",
    style: "Vanguard Guard walls behind bulwark plating — nothing gets through.",
    income: 4,
    pts: 16,
    deck: () => [
      ...guardWalls().slice(0, 4),
      ...cheapest(officialTcgCards.filter((c) => getCardRules(c).type === "TERRAIN" && terrainRule(c) === "bulwark"), 2),
    ],
  },
  {
    id: "zero",
    name: "SEAT ZERO",
    title: "The Architect",
    style: "Apex legendaries and unstable bombs — the council's final seat.",
    income: 5,
    pts: 20,
    deck: () => strongest(officialTcgCards.filter((c) => c.faction !== "BOSS" && getCardRules(c).type === "UNIT"), 6),
  },
];

export const personaById = (id) => COUNCIL_PERSONAS.find((p) => p.id === id) || COUNCIL_PERSONAS[0];

const sideName = { you: "YOU", net: "NET" };

/* terrain doctrine by faction — uniform rules that ride under the printed text */
export const terrainRule = (card) => {
  if (card.name.startsWith("WASTELAND RUNNER")) return "node"; // the Runner network — +2 scrap while you hold the lane
  if (card.faction === "RIOTBALL") return "riot"; // coin flip on entry
  return "bulwark"; // bastions and perimeters — +3 HP plating
};

export function newMatch(deckIds, personaId = "sax") {
  const persona = personaById(personaId);
  const youHand = deckIds.map(cardById).filter(Boolean);
  const netHand = persona.deck();
  const blank = (hand) => ({
    hp: MATCH_HP,
    scrap: SCRAP_START,
    spent: 0,
    hand,
    lanes: [null, null, null],
    terrain: [null, null, null],
    terrainJammed: [false, false, false],
  });
  return {
    round: 1,
    over: false,
    winner: null,
    persona,
    you: blank(youHand),
    net: blank(netHand),
    log: [`${persona.name} — ${persona.title} — takes the council seat. Commanders at ${MATCH_HP}. Their style: ${persona.style}`],
  };
}

/* signal check: 2+ other same-faction units in play */
const signalOn = (side, unit) =>
  side.lanes.filter((u) => u && u.iid !== unit.iid && u.card.faction === unit.card.faction).length >= 2;

const effAtk = (side, unit) => {
  let atk = (unit.unstable ? 20 : num(unit.card.stats.atk)) + unit.tempAtk;
  if (hasKw(unit, "Signal") && signalOn(side, unit)) atk += kwValue(unit, "Signal");
  if (hasKw(unit, "Overclock") && side.spent === 0) atk += 2; // banked scrap overclocks
  return Math.max(0, atk);
};

export function playCard(match, side, handIndex, laneIndex) {
  const s = match[side];
  const card = s.hand[handIndex];
  if (!card || match.over) return { ok: false, reason: "No card there." };
  const cost = matchCost(card);
  if (cost > s.scrap) return { ok: false, reason: `Needs ${cost} SCRAP — you hold ${s.scrap}.` };
  const terrain = isTerrainCard(card);
  if (terrain && s.terrain[laneIndex]) return { ok: false, reason: "That lane already holds terrain." };
  if (!terrain && s.lanes[laneIndex]) return { ok: false, reason: "That lane is occupied." };

  s.hand = s.hand.filter((_, i) => i !== handIndex);
  s.scrap -= cost;
  s.spent += cost;

  if (terrain) {
    s.terrain[laneIndex] = card;
    const rule = terrainRule(card);
    match.log.unshift(
      `${sideName[side]} set terrain — ${card.name.split("—")[0].trim()} holds lane ${laneIndex + 1} (${rule === "riot" ? "COIN FLIP on entry" : rule === "bulwark" ? "+3 plating to units posted here" : "+2 scrap while you hold this lane"}).`,
    );
    return { ok: true };
  }

  const unit = makeUnit(card);
  // bulwark plating: vanguard terrain hardens units posted to its lane (unless jammed)
  if (s.terrain[laneIndex] && !s.terrainJammed[laneIndex] && terrainRule(s.terrain[laneIndex]) === "bulwark") {
    unit.hp += 3;
    unit.bonusHp += 3;
  }
  // riot coin flip: chaos terrain swings the entry (unless jammed)
  if (s.terrain[laneIndex] && !s.terrainJammed[laneIndex] && terrainRule(s.terrain[laneIndex]) === "riot") {
    if (Math.random() < 0.5) {
      unit.tempAtk += 3;
      match.log.unshift(`${card.name.split(",")[0]} wins the riot flip — +3 ATK.`);
    } else {
      unit.hp = Math.max(1, unit.hp - 2);
      match.log.unshift(`${card.name.split(",")[0]} loses the riot flip — −2 HP.`);
    }
  }
  s.lanes[laneIndex] = unit;
  match.log.unshift(`${sideName[side]} deploy ${card.name.split(",")[0]} to lane ${laneIndex + 1} (${cost} scrap).`);
  return { ok: true };
}

/* Jam — blank an enemy unit's keywords, or silence an enemy terrain's effect,
   until the round ends */
export function jamUnit(match, side, attackerLane, targetLane, targetKind = "unit") {
  const s = match[side];
  const enemy = match[side === "you" ? "net" : "you"];
  const jammer = s.lanes[attackerLane];
  if (!jammer || jammer.jammed || !hasKw(jammer, "Jam")) return { ok: false };
  if (targetKind === "terrain") {
    if (!enemy.terrain[targetLane] || enemy.terrainJammed[targetLane]) return { ok: false };
    enemy.terrainJammed[targetLane] = true;
    match.log.unshift(`${sideName[side]} JAM the terrain in lane ${targetLane + 1} — its effect is dead air this round.`);
    return { ok: true };
  }
  const target = enemy.lanes[targetLane];
  if (!target) return { ok: false };
  target.jammed = true;
  match.log.unshift(`${sideName[side]} JAM ${target.card.name.split(",")[0]} — its keywords are dead air this round.`);
  return { ok: true };
}

/* Crowd bribe — pay 2 scrap to force an enemy riot lane's coin flip against the
   unit posted there: heads it surges +3 ATK, tails it takes 2 damage */
export function riotTrigger(match, side, lane) {
  const s = match[side];
  const enemy = match[side === "you" ? "net" : "you"];
  const terrain = enemy.terrain[lane];
  const posted = enemy.lanes[lane];
  if (match.over || !terrain || terrainRule(terrain) !== "riot" || !posted || enemy.terrainJammed[lane]) return { ok: false, reason: "No live riot lane there." };
  if (s.scrap < 2) return { ok: false, reason: "Crowd bribes cost 2 SCRAP." };
  s.scrap -= 2;
  s.spent += 2;
  const name = posted.card.name.split(",")[0];
  if (Math.random() < 0.5) {
    posted.tempAtk += 3;
    match.log.unshift(`CROWD BRIBE backfires — the lane roars for ${name}: +3 ATK.`);
  } else {
    posted.hp -= 2;
    match.log.unshift(`CROWD BRIBE — the mob turns on ${name}: −2 HP.`);
  }
  return { ok: true };
}

/* the council plays: strongest affordable units into threatened lanes, terrain
   behind them, and a Jam on your scariest keyword carrier */
function aiTakeTurn(match) {
  const s = match.net;
  let guard = 8;
  while (guard > 0) {
    guard -= 1;
    const affordable = s.hand
      .map((card, i) => ({ card, i }))
      .filter(({ card }) => num(card.stats.scrap) <= s.scrap);
    if (!affordable.length) break;
    // units first, priciest viable play — the council commits its best
    affordable.sort((a, b) => num(b.card.stats.scrap) - num(a.card.stats.scrap));
    const pick = affordable[0];
    const terrain = isTerrainCard(pick.card);
    let lane = -1;
    if (terrain) {
      lane = s.terrain.findIndex((t, i) => !t && s.lanes[i]);
      if (lane === -1) lane = s.terrain.findIndex((t) => !t);
    } else {
      // doctrine: wall off real threats (5+ ATK), otherwise take an open lane
      // and swing at the commander — the council stops trading with fodder
      const open = s.lanes.map((u, i) => (u ? -1 : i)).filter((i) => i !== -1);
      const threats = open.filter((i) => match.you.lanes[i] && effAtk(match.you, match.you.lanes[i]) >= 5);
      const face = open.filter((i) => !match.you.lanes[i]);
      if (threats.length) lane = threats[0];
      else if (face.length) lane = face[0];
      else if (open.length) lane = open[0];
    }
    if (lane === -1) break;
    const res = playCard(match, "net", pick.i, lane);
    if (!res.ok) break;
  }
  // counter-play: bribe the player's riot lanes against their own posted units
  match.you.terrain.forEach((t, i) => {
    if (t && terrainRule(t) === "riot" && match.you.lanes[i] && s.scrap >= 4 && !match.you.terrainJammed[i]) {
      riotTrigger(match, "net", i);
    }
  });
  // jam priority: strongest keyword carrier, else choke a node's income
  const jammerLane = s.lanes.findIndex((u) => u && !u.jammed && hasKw(u, "Jam"));
  if (jammerLane !== -1) {
    const targetLane = match.you.lanes
      .map((u, i) => ({ u, i }))
      .filter(({ u }) => u && !u.jammed && getCardRules(u.card).keywords.length)
      .sort((a, b) => effAtk(match.you, b.u) - effAtk(match.you, a.u))[0]?.i;
    if (targetLane !== undefined) {
      jamUnit(match, "net", jammerLane, targetLane);
    } else {
      const nodeLane = match.you.terrain.findIndex((t, i) => t && terrainRule(t) === "node" && !match.you.terrainJammed[i]);
      if (nodeLane !== -1) jamUnit(match, "net", jammerLane, nodeLane, "terrain");
    }
  }
}

function resolveCombat(match) {
  const events = [];
  for (const side of ["you", "net"]) {
    const atkSide = match[side];
    const defSide = match[side === "you" ? "net" : "you"];
    atkSide.lanes.forEach((unit, lane) => {
      if (!unit || unit.hp <= 0) return; // the fallen don't swing
      const power = effAtk(atkSide, unit);
      if (power <= 0) return;
      // guard wall, re-evaluated per strike: an active Guard unit intercepts
      // every attack while it still stands
      const guardLane = defSide.lanes.findIndex((u) => u && u.hp > 0 && !u.jammed && hasKw(u, "Guard"));
      const target = guardLane !== -1 ? defSide.lanes[guardLane] : defSide.lanes[lane];
      const name = unit.card.name.split(",")[0];
      if (target) {
        target.hp -= power;
        events.push(`${name} hits ${target.card.name.split(",")[0]} for ${power}${guardLane !== -1 && guardLane !== lane ? " (GUARD intercepts)" : ""}.`);
        if (target.hp <= 0) {
          // bounty pays the killer, salvage refunds the fallen
          if (hasKw(unit, "Bounty")) {
            atkSide.scrap = Math.min(SCRAP_CAP, atkSide.scrap + kwValue(unit, "Bounty"));
            events.push(`BOUNTY — ${name} collects +${kwValue(unit, "Bounty")} scrap.`);
          }
          if (hasKw(target, "Salvage")) {
            defSide.scrap = Math.min(SCRAP_CAP, defSide.scrap + kwValue(target, "Salvage"));
            events.push(`SALVAGE — ${target.card.name.split(",")[0]} returns +${kwValue(target, "Salvage")} scrap.`);
          }
        }
      } else {
        defSide.hp -= power;
        events.push(side === "you" ? `${name} strikes the NET Commander for ${power}.` : `${name} strikes YOUR Commander for ${power}.`);
      }
    });
  }
  // clear the dead
  for (const side of ["you", "net"]) {
    match[side].lanes = match[side].lanes.map((u) => {
      if (u && u.hp <= 0) {
        events.push(`${u.card.name.split(",")[0]} is destroyed.`);
        return null;
      }
      return u;
    });
  }
  return events;
}

export function endRound(match) {
  if (match.over) return match;
  // corrupted upkeep — unstable power bills its own commander
  for (const side of ["you", "net"]) {
    match[side].lanes.forEach((u) => {
      if (u && !u.jammed && (hasKw(u, "Corrupted") || u.unstable)) {
        const dmg = u.unstable && !hasKw(u, "Corrupted") ? 3 : kwValue(u, "Corrupted");
        match[side].hp -= dmg;
        match.log.unshift(`${u.unstable ? "UNSTABLE" : "CORRUPTED"} — ${u.card.name.split(",")[0]} bills the ${sideName[side]} Commander ${dmg}.`);
      }
    });
  }
  aiTakeTurn(match);
  const events = resolveCombat(match);
  [...events].reverse().forEach((e) => match.log.unshift(e));

  // node terrain income: hold the lane, draw the network (unless jammed)
  for (const side of ["you", "net"]) {
    match[side].terrain.forEach((t, i) => {
      if (t && terrainRule(t) === "node" && match[side].lanes[i] && !match[side].terrainJammed[i]) {
        match[side].scrap = Math.min(SCRAP_CAP, match[side].scrap + 2);
      }
    });
  }

  const youDead = match.you.hp <= 0;
  const netDead = match.net.hp <= 0;
  if (youDead || netDead) {
    match.over = true;
    match.winner = youDead && netDead ? "draw" : netDead ? "you" : "net";
  } else if (match.round >= MATCH_ROUNDS) {
    match.over = true;
    match.winner = match.you.hp === match.net.hp ? "draw" : match.you.hp > match.net.hp ? "you" : "net";
    match.log.unshift(`ROUND CAP — commanders compared at ${match.you.hp} vs ${match.net.hp}.`);
  }
  if (!match.over) {
    match.round += 1;
    match.log.unshift(`ROUND ${match.round} — +${SCRAP_PER_ROUND + 1} scrap to you, +${match.persona.income} to ${match.persona.name}.`);
  }
  for (const side of ["you", "net"]) {
    // each council seat bankrolls its war differently — the climb gets richer
    const income = side === "net" ? match.persona.income : SCRAP_PER_ROUND + 1;
    match[side].scrap = Math.min(SCRAP_CAP, match[side].scrap + income);
    match[side].spent = 0;
    match[side].terrainJammed = [false, false, false];
    match[side].lanes.forEach((u) => {
      if (u) u.jammed = false;
    });
  }
  match.log = match.log.slice(0, 30);
  return match;
}

/* read-only helpers for the board UI */
export const lanePower = (match, side, lane) => {
  const u = match[side].lanes[lane];
  return u ? effAtk(match[side], u) : 0;
};
export const unitKeywords = (unit) => getCardRules(unit.card).keywords;
export const unitJammed = (unit) => unit.jammed;
export { effAtk, hasKw };
